/**
 * Die Feinbodenform der Moore — Aufbau, Eingabepruefung, Vorschlaege und die
 * daraus abgeleitete Hauptbodenform.
 *
 * Moore werden seit 2011 in Anlehnung an die Greifswalder Moorklassifikation
 * bezeichnet (SEA 95, Teil A, Abschnitt 2). Ihre Feinbodenform wird FREI
 * GEBILDET und steht deshalb — anders als bei den mineralischen Boeden — NICHT
 * in der Liste `Feinbodenformen.json`:
 *
 *     Substratfolgetyp + Moortyp + "." + Naehrkraftstufe
 *
 * Beispiele aus SEA Teil D, S.461:
 *     vhRi.K      = Volltorf-Ried.Kraeftig
 *     kihFe.RC    = Kalkhalbtorf-Fen.Reich
 *     SakvmFe.KC  = Sand-Ankalkvollmodd-Fen.Kraeftig
 *     LkvhEF.RC   = Lehm-Kalkvolltorf-Erdfen.Reich
 *
 * NICHT hierher gehoeren die Vorsaetze `a` (ausgetorft), `q` (quellig),
 * `s` (uebersandet) und `w`: Die stehen im eigenen Feld "Praefix"
 * (`bodenform.bf[].prefix`, siehe FBF_PREFIX_REGEX in data-model.ts und die
 * Klartexte in help/fbf-praefixe.ts). Dass `q` dort UND weiter unten als
 * Substrat Quebb vorkommt, ist deshalb kein Widerspruch — es sind zwei Felder.
 *
 * Diese Datei ist die EINZIGE Stelle mit der Moor-Systematik; vorher stand die
 * Eingabepruefung wortgleich in zwei Komponenten (Boden-Reiter und
 * Kurzprotokoll) und waere beim naechsten Anfassen auseinandergelaufen.
 */

/**
 * Die Substrate des Substratfolgetyps (SEA Teil A, Darst. A 2-1).
 *
 * Sedentaer (Torfsubstrate): ah = Antorf, akah = Ankalkantorf, kah = Kalkantorf,
 * ih = Halbtorf, akih = Ankalkhalbtorf, kih = Kalkhalbtorf, vh = Volltorf,
 * akvh = Ankalkvolltorf, kvh = Kalkvolltorf, ph = Reintorf, akph = Ankalkreintorf.
 *
 * Sedimentaer (Modd-Substrate): d = Fledd, akd = Ankalkfledd, kd = Kalkfledd,
 * q = Quebb, akq = Ankalkquebb, kq = Kalkquebb, mk = Moddkalk, im = Halbmodd,
 * akim = Ankalkhalbmodd, kim = Kalkhalbmodd, vm = Vollmodd, akvm = Ankalkvollmodd,
 * k = Reinkalk.
 *
 * Reihenfolge: laengere Kuerzel zuerst, damit die Eingabepruefung "akvh" nicht
 * als "a" + "kvh" o.ae. zerlegt.
 */
export const MOOR_SUBSTRATE = [
	'ah', 'akah', 'kah',
	'ih', 'akih', 'kih',
	'vh', 'akvh', 'kvh',
	'ph', 'akph',
	'd', 'akd', 'kd',
	'q', 'akq', 'kq',
	'mk', 'k',
	'im', 'akim', 'kim',
	'vm', 'akvm',
] as const;

/**
 * Die vier Moortypen nach SUCCOW (SEA Teil A, Darst. A 2-7); sie richten sich
 * nach dem Vererdungsgrad in den ersten 40 cm des Moores.
 */
export const MOOR_TYPEN = ['Ri', 'Fe', 'EF', 'Ml'] as const;

/**
 * Die Naehrkraftstufen der Moore (SEA Teil A, Darst. A 2-9): R = reich,
 * K = kraeftig, M = mittel, Z = ziemlich arm, A = arm, D = sehr arm (dystroph).
 * Das angehaengte C kennzeichnet die kalkhaltige Auspraegung.
 */
export const MOOR_NAEHRKRAFTSTUFEN = ['R', 'K', 'M', 'MC', 'Z', 'ZC', 'A', 'AC', 'D'] as const;

/**
 * Vorsatz des Substratfolgetyps: Er sagt, welcher Mineralboden unter dem Moor
 * liegt, wenn die organische Auflage nur 40 bis 80 cm maechtig ist (SEA Teil A,
 * Abschnitt 2.1). Ohne Vorsatz ist die organische Auflage > 80 cm maechtig.
 *
 * Jeder Vorsatz traegt die Hauptbodenform, die daraus folgt: `s GM`
 * (Sand-Gleymoor) und `l GM` (Lehm-Gleymoor). `dL` (Decklehm) ist hier
 * ausdruecklich NICHT zugelassen (Nutzer-Entscheidung 11.09.2026).
 *
 * Bis zum 24.09.2026 standen hier `SM` und `LM` - eigene Kuerzel, die am
 * 08.04.2026 zusammen mit `WM` als Hauptbodenformen eingefuehrt wurden. Das war
 * ein Fehler (Nutzer): SM IST s GM und LM IST l GM. Die drei Kuerzel sind
 * abgeschafft; der Vorsatz an der Feinbodenform bleibt.
 */
export const MOOR_SUBSTRATFOLGE_VORSAETZE = [
	{ code: 'S', bedeutung: 'Sand-', hbf: 's GM' },
	{ code: 'L', bedeutung: 'Lehm-', hbf: 'l GM' },
] as const;

/** Hauptbodenform ohne Vorsatz: das Moor selbst. */
export const MOOR_HBF_OHNE_VORSATZ = 'M';

/**
 * Die Hauptbodenformen, unter denen Moor-Feinbodenformen in Frage kommen. Sie
 * stehen in KEINER Liste (weder Feinbodenformen noch Katalog) und kommen nur
 * von hier als Zusatzvorschlaege ins Feld "Hauptbodenform".
 */
export const MOOR_HBFS = [MOOR_HBF_OHNE_VORSATZ, ...MOOR_SUBSTRATFOLGE_VORSAETZE.map(v => v.hbf)] as const;

const alternativen = (werte: readonly string[]) => werte.join('|');

/**
 * Eingabepruefung fuer eine Moor-Feinbodenform. Als Zeichenkette, weil das
 * Typeahead-Feld sie so erwartet (`otherValuesRegex`).
 */
export const MOOR_FBF_REGEX =
	`^(${alternativen(MOOR_SUBSTRATFOLGE_VORSAETZE.map(v => v.code))})?` +
	`(${alternativen(MOOR_SUBSTRATE)})+` +
	`(${alternativen(MOOR_TYPEN)})` +
	`\\.(${alternativen(MOOR_NAEHRKRAFTSTUFEN)})$`;

/** Der Satz, der bei falscher Eingabe unter dem Feld steht. */
export const MOOR_FBF_REGEX_MELDUNG =
	'Moor-FBF: [S/L] + Substrat + Moortyp (Ri/Fe/EF/Ml) + . + Nährkraftstufe (R/K/M/Z/A/D, ggf. +C)';

/** Ist der Wert nach der Moor-Systematik gebildet? */
export function istMoorFbf(fbf: string | undefined | null): boolean {
	const wert = (fbf ?? '').trim();
	return wert.length > 0 && new RegExp(MOOR_FBF_REGEX).test(wert);
}

/**
 * Die Hauptbodenform zu einer Moor-Feinbodenform.
 *
 * Regel (Nutzer-Entscheidung 24.09.2026): Der Vorsatz des Substratfolgetyps
 * entscheidet — `S` → `s GM` (Sand-Gleymoor), `L` → `l GM` (Lehm-Gleymoor),
 * ohne Vorsatz `M` (Moor).
 *
 * Liefert `undefined`, wenn der Wert keine Moor-Feinbodenform ist. Das ist
 * wichtig: Eine mineralische FBF bekommt ihre Hauptbodenform aus der Liste und
 * darf hier auf keinen Fall mit `M` ueberschrieben werden.
 */
export function hbfAusMoorFbf(fbf: string | undefined | null): string | undefined {
	const wert = (fbf ?? '').trim();
	if (!istMoorFbf(wert)) return undefined;
	const vorsatz = MOOR_SUBSTRATFOLGE_VORSAETZE.find(v => wert.startsWith(v.code));
	return vorsatz ? vorsatz.hbf : MOOR_HBF_OHNE_VORSATZ;
}

/**
 * Erzeugt die Vorschlaege fuer das Feinbodenform-Feld. Weil die Moor-Formen
 * frei gebildet werden, gibt es keine Liste, aus der man sie nehmen koennte —
 * sie werden aus der Systematik ausmultipliziert.
 *
 * Zu jedem Basis-Substrat steht der wahrscheinlichste Moortyp vorn, damit die
 * ueblichen Kombinationen zuerst erscheinen.
 */
export function generateMoorFBFSuggestions(): string[] {
	const vorsaetze = ['', ...MOOR_SUBSTRATFOLGE_VORSAETZE.map(v => v.code)];
	const substrate = vorsaetze.flatMap(p => MOOR_SUBSTRATE.map(s => p + s));

	// Wahrscheinlicher Moortyp je Basis-Substrat (kommt zuerst)
	const wahrscheinlichBasis: Record<string, string[]> = {
		'ph': ['Ri'], 'akph': ['Ri'],
		'vh': ['Fe'], 'akvh': ['Fe'], 'kvh': ['Fe'],
		'ih': ['EF', 'Ml'], 'akih': ['EF', 'Ml'], 'kih': ['EF', 'Ml'],
		'ah': ['Ri', 'Fe'], 'akah': ['Ri', 'Fe'], 'kah': ['Ri', 'Fe'],
	};
	const wahrscheinlich: Record<string, string[]> = {};
	for (const [basis, typen] of Object.entries(wahrscheinlichBasis)) {
		for (const p of vorsaetze) wahrscheinlich[p + basis] = typen;
	}

	const vorn: string[] = [];
	const rest: string[] = [];
	for (const s of substrate) {
		const bevorzugt = wahrscheinlich[s] || [];
		for (const typ of MOOR_TYPEN) {
			for (const nk of MOOR_NAEHRKRAFTSTUFEN) {
				const fbf = `${s}${typ}.${nk}`;
				if (bevorzugt.includes(typ)) vorn.push(fbf);
				else rest.push(fbf);
			}
		}
	}
	return [...vorn, ...rest];
}

let vorschlaegeZwischenspeicher: string[] | undefined;

/**
 * Moor-Vorschlaege fuer das Feinbodenform-Feld, passend zur gewaehlten
 * Hauptbodenform: solange keine gewaehlt ist oder eine Moor-Hauptbodenform
 * dasteht, werden sie angeboten — sonst nicht.
 */
export function moorFbfVorschlaegeFuer(hbf: string | undefined | null): string[] {
	const wert = (hbf ?? '').trim();
	if (wert && !(MOOR_HBFS as readonly string[]).includes(wert)) return [];
	return (vorschlaegeZwischenspeicher ??= generateMoorFBFSuggestions());
}
